/**
 * Client-side tool keys and helpers for dashboard tools catalog.
 * Presentation/ranking comes from GET /api/dashboard-tools; this file owns
 * stable ids and mapping API rows → modal/handler payloads.
 */

import {
  resizeIcon,
  aiShadowIcon as shadowIcon,
  outlineIcon,
  rotateIcon,
  flipIcon,
  colorPickerIcon as backgroundColorIcon,
  backgroundIcon,
  aiUpscaleIcon,
  aiEnhanceIcon,
  aiRelightIcon,
  layersIcon,
  moreIcon,
  downloadNavIcon,
} from "../../icons/assets";
import {
  CONVERSION_TOOL_IDS,
  CONVERSION_TOOL_SUBTITLES,
  IMAGE_CONVERSION_CATEGORY_SLUG,
  imageConversionFallbackSection,
} from "../tools/conversionTools";

export const TRENDING_TOOL_IDS = {
  EDIT_IMAGES: "edit_images_ai",
  PASSPORT_PHOTOS: "passport_photos",
  PRODUCT_CATALOG: "product_catalog",
  AUTOMOBILE_CATALOG: "automobile_catalog",
  FOOD_CATALOG: "food_catalog",
};

export const EDITING_TOOL_IDS = {
  RESIZE: "resize",
  SHADOW: "shadow",
  OUTLINE: "outline",
  BACKGROUND_COLOR: "background_color",
  WHITE_BACKGROUND: "white_background",
  BLACK_BACKGROUND: "black_background",
  ROTATE: "rotate",
  FLIP: "flip",
  PADDING: "padding",
  CROP: "crop",
  CIRCLE_CROP: "circle_crop",
  WATERMARK_MAKER: "watermark_maker",
  BLUR: "blur",
  GAUSSIAN_BLUR: "gaussian_blur",
  PIXEL_BLUR: "pixel_blur",
  MOTION_BLUR: "motion_blur",
  BOKEH_BLUR: "bokeh_blur",
  BACKGROUND_BLUR: "background_blur",
};

export const DOCUMENT_TOOL_IDS = {
  SIGN_PDF: "sign_pdf",
  PDF_WATERMARKER: "pdf_watermarker",
  PDF_PAGE_NUMBERS: "pdf_page_numbers",
  WORD_WATERMARKER: "word_watermarker",
};

export const AI_TOOL_IDS = {
  BACKGROUND_IMAGE: "ai_background_image",
  SHADOW: "ai_shadow",
  RECOLOR: "ai_recolor",
  REMOVE_OBJECT: "ai_remove_object",
  REMOVE_TEXT: "ai_remove_text",
  EXPAND: "ai_expand",
  ENHANCE: "ai_enhance",
  UPSCALE: "ai_upscale",
  RELIGHT: "ai_relight",
  REMOVE_WATERMARK: "ai_remove_watermark",
  BACKGROUND_REMOVER: "ai_background_remover",
  SHADOW_REMOVER: "ai_shadow_remover",
  OLD_PHOTO_RESTORE: "ai_old_photo_restore",
  COLORIZER: "ai_colorizer",
  UNBLUR: "ai_unblur",
  REPLACE_OBJECT: "ai_replace_object",
  REPLACE_TEXT: "ai_replace_text",
  FACE_SWAP: "ai_face_swap",
};

export const PHOTO_STUDIO_TOOL_IDS = {
  PASSPORT_PHOTO_MAKER: "ai_passport_photo_maker",
};

/**
 * AI tools that stay live. Every other AI Tools / AI Photo Studio card
 * shows Coming soon and does not open.
 */
export const LIVE_AI_TOOL_KEYS = new Set([
  AI_TOOL_IDS.BACKGROUND_REMOVER,
  AI_TOOL_IDS.REMOVE_OBJECT,
  AI_TOOL_IDS.REPLACE_OBJECT,
  AI_TOOL_IDS.EXPAND,
  AI_TOOL_IDS.UPSCALE,
  PHOTO_STUDIO_TOOL_IDS.PASSPORT_PHOTO_MAKER,
]);

const AI_CATALOG_TOOL_KEYS = new Set([
  ...Object.values(AI_TOOL_IDS),
  ...Object.values(PHOTO_STUDIO_TOOL_IDS),
]);

export function isAiToolComingSoon(toolKey) {
  return AI_CATALOG_TOOL_KEYS.has(toolKey) && !LIVE_AI_TOOL_KEYS.has(toolKey);
}

/** Fallback card taglines when API subtitle is empty */
export const EDITING_TOOL_SUBTITLES = {
  [EDITING_TOOL_IDS.RESIZE]:
    "Professional image resizing designed for modern creative workflows.",
  [EDITING_TOOL_IDS.SHADOW]:
    "Add natural drop shadows to cutout products in a few clicks.",
  [EDITING_TOOL_IDS.OUTLINE]:
    "Add clean outlines to cutout subjects in a few clicks.",
  [EDITING_TOOL_IDS.BACKGROUND_COLOR]:
    "Replace the background with a solid color in a few clicks.",
  [EDITING_TOOL_IDS.WHITE_BACKGROUND]:
    "Apply a clean white background with shade options in a few clicks.",
  [EDITING_TOOL_IDS.BLACK_BACKGROUND]:
    "Apply a clean black background with shade options in a few clicks.",
  [EDITING_TOOL_IDS.ROTATE]:
    "Rotate your image with instant preview presets.",
  [EDITING_TOOL_IDS.FLIP]:
    "Flip your image horizontally with instant preview presets.",
  [EDITING_TOOL_IDS.PADDING]:
    "Add space around your image with per-side padding controls.",
  [EDITING_TOOL_IDS.CROP]:
    "Trim your image to the perfect frame with drag-and-drop crop controls.",
  [EDITING_TOOL_IDS.CIRCLE_CROP]:
    "Crop your image into a perfect circle with drag-and-drop controls.",
  [EDITING_TOOL_IDS.WATERMARK_MAKER]:
    "Add logos and text watermarks to your images.",
  [EDITING_TOOL_IDS.BLUR]:
    "All blur styles in one place — whole image, or subject only with Remove Background.",
  [EDITING_TOOL_IDS.GAUSSIAN_BLUR]:
    "Soft Gaussian blur with mix, edge preserve, and full / subject / background targeting.",
  [EDITING_TOOL_IDS.PIXEL_BLUR]:
    "Hex, square, circle, or diamond mosaic with cell size, gap, softness, and mix.",
  [EDITING_TOOL_IDS.MOTION_BLUR]:
    "Directional motion blur for speed, action, and dynamic product shots.",
  [EDITING_TOOL_IDS.BOKEH_BLUR]:
    "Creamy bokeh-style blur for portraits, products, and dreamy depth of field.",
  [EDITING_TOOL_IDS.BACKGROUND_BLUR]:
    "Blur only the background — subject stays sharp. Pick any blur style.",
};

export const DOCUMENT_TOOL_SUBTITLES = {
  [DOCUMENT_TOOL_IDS.SIGN_PDF]:
    "Add your signature to a PDF and download.",
  [DOCUMENT_TOOL_IDS.PDF_WATERMARKER]:
    "Add text and logo watermarks to PDF documents.",
  [DOCUMENT_TOOL_IDS.PDF_PAGE_NUMBERS]:
    "Add page numbers to your PDF and download.",
  [DOCUMENT_TOOL_IDS.WORD_WATERMARKER]:
    "Add text and logo watermarks to Word documents.",
};

export const AI_TOOL_SUBTITLES = {
  [AI_TOOL_IDS.BACKGROUND_IMAGE]:
    "Generate or replace backgrounds with AI in a few clicks.",
  [AI_TOOL_IDS.SHADOW]: "Add natural AI drop shadows to cutout products.",
  [AI_TOOL_IDS.RECOLOR]: "Recolor objects and regions with AI prompts.",
  [AI_TOOL_IDS.REMOVE_OBJECT]: "Erase unwanted objects from your image with AI.",
  [AI_TOOL_IDS.REMOVE_TEXT]: "Clean text and overlays from photos with AI.",
  [AI_TOOL_IDS.EXPAND]: "Outpaint and expand your canvas with AI.",
  [AI_TOOL_IDS.ENHANCE]: "Improve clarity and detail with AI enhancement.",
  [AI_TOOL_IDS.UPSCALE]: "Upscale images to higher resolution with AI.",
  [AI_TOOL_IDS.RELIGHT]: "Adjust lighting and mood with AI relighting.",
  [AI_TOOL_IDS.REMOVE_WATERMARK]: "Remove watermarks and logos with AI cleanup.",
  [AI_TOOL_IDS.BACKGROUND_REMOVER]:
    "Remove the background cleanly and keep a sharp cutout.",
  [AI_TOOL_IDS.SHADOW_REMOVER]:
    "Clean unwanted shadows and even out the lighting.",
  [AI_TOOL_IDS.OLD_PHOTO_RESTORE]:
    "Repair scratches, fade, and noise in old photos.",
  [AI_TOOL_IDS.COLORIZER]:
    "Bring black-and-white photos to life with natural color.",
  [AI_TOOL_IDS.UNBLUR]: "Sharpen soft or motion-blurred photos with AI.",
  [AI_TOOL_IDS.REPLACE_OBJECT]:
    "Swap an object for something new while matching the scene.",
  [AI_TOOL_IDS.REPLACE_TEXT]:
    "Change text in the image and rebuild the background.",
  [AI_TOOL_IDS.FACE_SWAP]: "Swap faces between photos with a natural blend.",
};

export const PHOTO_STUDIO_TOOL_SUBTITLES = {
  [PHOTO_STUDIO_TOOL_IDS.PASSPORT_PHOTO_MAKER]:
    "Create official passport and ID photos with AI.",
};

export { CONVERSION_TOOL_IDS, CONVERSION_TOOL_SUBTITLES };

/** Dashboard Editing Tools card icons (editor left-nav assets) */
export const EDITING_TOOL_ICONS = {
  [EDITING_TOOL_IDS.RESIZE]: resizeIcon,
  [EDITING_TOOL_IDS.SHADOW]: shadowIcon,
  [EDITING_TOOL_IDS.OUTLINE]: outlineIcon,
  [EDITING_TOOL_IDS.BACKGROUND_COLOR]: backgroundColorIcon,
  [EDITING_TOOL_IDS.WHITE_BACKGROUND]: backgroundColorIcon,
  [EDITING_TOOL_IDS.BLACK_BACKGROUND]: backgroundColorIcon,
  [EDITING_TOOL_IDS.ROTATE]: rotateIcon,
  [EDITING_TOOL_IDS.FLIP]: flipIcon,
  [EDITING_TOOL_IDS.PADDING]: resizeIcon,
  [EDITING_TOOL_IDS.CROP]: resizeIcon,
  [EDITING_TOOL_IDS.CIRCLE_CROP]: resizeIcon,
  [EDITING_TOOL_IDS.WATERMARK_MAKER]: moreIcon,
  [EDITING_TOOL_IDS.BLUR]: backgroundIcon,
  [EDITING_TOOL_IDS.GAUSSIAN_BLUR]: backgroundIcon,
  [EDITING_TOOL_IDS.PIXEL_BLUR]: backgroundIcon,
  [EDITING_TOOL_IDS.MOTION_BLUR]: backgroundIcon,
  [EDITING_TOOL_IDS.BOKEH_BLUR]: backgroundIcon,
  [EDITING_TOOL_IDS.BACKGROUND_BLUR]: backgroundIcon,
};

/** Dashboard Document Tools card icons */
export const DOCUMENT_TOOL_ICONS = {
  [DOCUMENT_TOOL_IDS.SIGN_PDF]: moreIcon,
  [DOCUMENT_TOOL_IDS.PDF_WATERMARKER]: moreIcon,
  [DOCUMENT_TOOL_IDS.PDF_PAGE_NUMBERS]: moreIcon,
  [DOCUMENT_TOOL_IDS.WORD_WATERMARKER]: moreIcon,
};

/** Dashboard AI Tools card icons */
export const AI_TOOL_ICONS = {
  [AI_TOOL_IDS.BACKGROUND_IMAGE]: backgroundIcon,
  [AI_TOOL_IDS.SHADOW]: shadowIcon,
  [AI_TOOL_IDS.RECOLOR]: backgroundColorIcon,
  [AI_TOOL_IDS.REMOVE_OBJECT]: layersIcon,
  [AI_TOOL_IDS.REMOVE_TEXT]: outlineIcon,
  [AI_TOOL_IDS.EXPAND]: resizeIcon,
  [AI_TOOL_IDS.ENHANCE]: aiEnhanceIcon,
  [AI_TOOL_IDS.UPSCALE]: aiUpscaleIcon,
  [AI_TOOL_IDS.RELIGHT]: aiRelightIcon,
  [AI_TOOL_IDS.REMOVE_WATERMARK]: moreIcon,
  [AI_TOOL_IDS.BACKGROUND_REMOVER]: backgroundIcon,
  [AI_TOOL_IDS.SHADOW_REMOVER]: shadowIcon,
  [AI_TOOL_IDS.OLD_PHOTO_RESTORE]: aiEnhanceIcon,
  [AI_TOOL_IDS.COLORIZER]: backgroundColorIcon,
  [AI_TOOL_IDS.UNBLUR]: aiUpscaleIcon,
  [AI_TOOL_IDS.REPLACE_OBJECT]: layersIcon,
  [AI_TOOL_IDS.REPLACE_TEXT]: outlineIcon,
  [AI_TOOL_IDS.FACE_SWAP]: moreIcon,
};

/** Dashboard AI Photo Studio card icons */
export const PHOTO_STUDIO_TOOL_ICONS = {
  [PHOTO_STUDIO_TOOL_IDS.PASSPORT_PHOTO_MAKER]: aiEnhanceIcon,
};

/** Dashboard Image Conversion card icons */
export const CONVERSION_TOOL_ICONS = {
  [CONVERSION_TOOL_IDS.HEIC_TO_JPG]: downloadNavIcon,
  [CONVERSION_TOOL_IDS.WEBP_TO_JPG]: downloadNavIcon,
  [CONVERSION_TOOL_IDS.PNG_TO_JPG]: downloadNavIcon,
  [CONVERSION_TOOL_IDS.JPG_TO_PNG]: downloadNavIcon,
  [CONVERSION_TOOL_IDS.WEBP_TO_PNG]: downloadNavIcon,
};

export const DEFAULT_CREDIT_TEXT = "1 credit per image";

/** Client overrides when catalog metadata is still clickable:false but the tool is shipped. */
const CLIENT_TOOL_OVERRIDES = {
  [AI_TOOL_IDS.RECOLOR]: {
    clickable: true,
    editorPanel: AI_TOOL_IDS.RECOLOR,
    action: "open_editor_panel",
  },
  [AI_TOOL_IDS.ENHANCE]: {
    clickable: true,
    editorPanel: AI_TOOL_IDS.ENHANCE,
    action: "open_editor_panel",
  },
  [AI_TOOL_IDS.BACKGROUND_IMAGE]: {
    clickable: true,
    editorPanel: AI_TOOL_IDS.BACKGROUND_IMAGE,
    action: "open_editor_panel",
  },
  [AI_TOOL_IDS.SHADOW]: {
    clickable: true,
    editorPanel: AI_TOOL_IDS.SHADOW,
    action: "open_editor_panel",
  },
  [AI_TOOL_IDS.REMOVE_OBJECT]: {
    clickable: true,
    editorPanel: AI_TOOL_IDS.REMOVE_OBJECT,
    action: "open_editor_panel",
  },
  [AI_TOOL_IDS.REMOVE_TEXT]: {
    clickable: true,
    editorPanel: AI_TOOL_IDS.REMOVE_TEXT,
    action: "open_editor_panel",
  },
  [AI_TOOL_IDS.UPSCALE]: {
    clickable: true,
    editorPanel: AI_TOOL_IDS.UPSCALE,
    action: "open_editor_panel",
  },
  [AI_TOOL_IDS.RELIGHT]: {
    clickable: true,
    editorPanel: AI_TOOL_IDS.RELIGHT,
    action: "open_editor_panel",
  },
  [AI_TOOL_IDS.REMOVE_WATERMARK]: {
    clickable: true,
    editorPanel: AI_TOOL_IDS.REMOVE_WATERMARK,
    action: "open_editor_panel",
  },
  [AI_TOOL_IDS.BACKGROUND_REMOVER]: {
    clickable: true,
    editorPanel: AI_TOOL_IDS.BACKGROUND_REMOVER,
    action: "open_editor_panel",
  },
  [AI_TOOL_IDS.SHADOW_REMOVER]: {
    clickable: true,
    editorPanel: AI_TOOL_IDS.SHADOW_REMOVER,
    action: "open_editor_panel",
  },
  [AI_TOOL_IDS.OLD_PHOTO_RESTORE]: {
    clickable: true,
    editorPanel: AI_TOOL_IDS.OLD_PHOTO_RESTORE,
    action: "open_editor_panel",
  },
  [AI_TOOL_IDS.COLORIZER]: {
    clickable: true,
    editorPanel: AI_TOOL_IDS.COLORIZER,
    action: "open_editor_panel",
  },
  [AI_TOOL_IDS.UNBLUR]: {
    clickable: true,
    editorPanel: AI_TOOL_IDS.UNBLUR,
    action: "open_editor_panel",
  },
  [AI_TOOL_IDS.REPLACE_OBJECT]: {
    clickable: true,
    editorPanel: AI_TOOL_IDS.REPLACE_OBJECT,
    action: "open_editor_panel",
  },
  [AI_TOOL_IDS.REPLACE_TEXT]: {
    clickable: true,
    editorPanel: AI_TOOL_IDS.REPLACE_TEXT,
    action: "open_editor_panel",
  },
  [AI_TOOL_IDS.FACE_SWAP]: {
    clickable: true,
    editorPanel: AI_TOOL_IDS.FACE_SWAP,
    action: "open_editor_panel",
  },
};

/**
 * Map an API tool row into the shape used by TrendingToolUploadModal / Dashboard.
 * @param {object} tool - API tool row (from placement join)
 * @returns {object}
 */
export function mapApiToolToClient(tool) {
  if (!tool) return null;
  const meta =
    tool.metadata && typeof tool.metadata === "object"
      ? tool.metadata
      : typeof tool.metadata === "string"
        ? (() => {
            try {
              return JSON.parse(tool.metadata);
            } catch {
              return {};
            }
          })()
        : {};
  const override = CLIENT_TOOL_OVERRIDES[tool.tool_key] || null;
  const comingSoon = isAiToolComingSoon(tool.tool_key);
  const clickable = comingSoon
    ? false
    : override?.clickable != null
      ? override.clickable
      : meta.clickable !== false;
  return {
    id: tool.tool_key,
    toolKey: tool.tool_key,
    cardLabel: tool.card_label,
    subtitle:
      tool.subtitle ||
      EDITING_TOOL_SUBTITLES[tool.tool_key] ||
      DOCUMENT_TOOL_SUBTITLES[tool.tool_key] ||
      CONVERSION_TOOL_SUBTITLES[tool.tool_key] ||
      AI_TOOL_SUBTITLES[tool.tool_key] ||
      PHOTO_STUDIO_TOOL_SUBTITLES[tool.tool_key] ||
      "",
    cardStyle: tool.card_style || "image",
    image: tool.card_image_url || null,
    processedCount: tool.processed_count,
    countSuffix: meta.count_suffix || "Processed",
    clickable,
    comingSoon,
    entrySource: meta.entry_source || null,
    modalTitle: meta.modal_title || tool.card_label,
    modalDescription: meta.modal_description || "",
    creditText: meta.credit_text || DEFAULT_CREDIT_TEXT,
    samplePool: meta.sample_pool || null,
    presetCategoryIds: Array.isArray(meta.preset_category_ids)
      ? meta.preset_category_ids
      : null,
    editorPanel: override?.editorPanel || meta.editor_panel || null,
    action: override?.action || meta.action || null,
    metadata: meta,
  };
}

/**
 * Format processed count for image-style tool cards.
 * @param {object} clientTool
 * @returns {string}
 */
export function formatToolProcessedCount(clientTool) {
  const n = clientTool?.processedCount;
  if (n == null || n === 0) return "";
  const suffix = clientTool.countSuffix || "Processed";
  return `${Number(n).toLocaleString()} ${suffix}`;
}

/**
 * Map API tool → DesignCategory card item.
 * @param {object} tool - API tool row
 * @param {string} categorySlug
 */
export function mapApiToolToCardItem(tool, categorySlug) {
  const client = mapApiToolToClient(tool);
  if (!client) return null;
  const isText = client.cardStyle === "text";
  return {
    toolId: client.id,
    toolKey: client.toolKey,
    categorySlug,
    cardStyle: client.cardStyle,
    image: isText ? null : client.image,
    icon: isText
      ? EDITING_TOOL_ICONS[client.toolKey] ||
        DOCUMENT_TOOL_ICONS[client.toolKey] ||
        CONVERSION_TOOL_ICONS[client.toolKey] ||
        AI_TOOL_ICONS[client.toolKey] ||
        PHOTO_STUDIO_TOOL_ICONS[client.toolKey] ||
        ""
      : "",
    label: client.cardLabel,
    count: isText ? "" : formatToolProcessedCount(client),
    resolution: isText ? client.subtitle || "" : client.subtitle,
    clickable: client.clickable,
    comingSoon: Boolean(client.comingSoon),
    clientTool: client,
  };
}

/** Live tools first, Coming soon after. Order within each group stays as returned. */
export function sortToolsAvailableFirst(items) {
  if (!Array.isArray(items) || items.length < 2) return items || [];
  const available = [];
  const soon = [];
  for (const item of items) {
    if (item?.comingSoon) soon.push(item);
    else available.push(item);
  }
  return available.concat(soon);
}

/**
 * Client catalog backfill for tools missing from GET /api/dashboard-tools.
 * Leave empty when DB is migrated; uncomment/add entries when needed.
 */
export const CLIENT_EDITING_TOOL_FALLBACKS = [
  // {
  //   tool_key: EDITING_TOOL_IDS.BLUR,
  //   card_label: "Blur",
  //   subtitle: EDITING_TOOL_SUBTITLES[EDITING_TOOL_IDS.BLUR],
  //   card_style: "text",
  //   sort_order: 13,
  //   metadata: {
  //     action: "open_editor_panel",
  //     editor_panel: EDITING_TOOL_IDS.BLUR,
  //     clickable: true,
  //   },
  // },
];

/**
 * Inject client fallbacks for tools not yet returned by GET /api/dashboard-tools.
 * @param {Array} sections - API dashboard tool sections
 * @returns {Array}
 */
export function mergeDashboardToolSections(sections) {
  const list = Array.isArray(sections) ? sections.map((section) => ({ ...section })) : [];
  if (!list.length) return list;
  const withEditing = list.map((section) => {
    if (section.slug !== "editing_tools" || !CLIENT_EDITING_TOOL_FALLBACKS.length) {
      return section;
    }
    const tools = Array.isArray(section.tools) ? [...section.tools] : [];
    const existingKeys = new Set(tools.map((t) => t.tool_key));
    for (const fallback of CLIENT_EDITING_TOOL_FALLBACKS) {
      if (existingKeys.has(fallback.tool_key)) continue;
      tools.push(fallback);
    }
    tools.sort(
      (a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0)
    );
    return { ...section, tools };
  });
  if (withEditing.some((section) => section.slug === IMAGE_CONVERSION_CATEGORY_SLUG)) {
    return withEditing;
  }
  return [...withEditing, imageConversionFallbackSection()];
}
